import defer from "lodash/defer"
import endsWith from "lodash/endsWith"
import filter from "lodash/filter"
import get from "lodash/get"
import isEmpty from "lodash/isEmpty"
import map from "lodash/map"
import Box from "component-lib/Base/components/Box"
import { Typography } from "component-lib/Base/components/Typography/Typography"
import { withLocalization } from "hoc-lib/localization"
import UrlStatusMessageContainer from "component-lib/TextImageVideo/components/UrlStatusMessage/UrlStatusMessageContainer"
import { NO, PATHS, LANG_OPTIONS_MAP } from "constants-lib/common"
import { userType } from "constants-lib/authentication"
import {
  BOOTSTRAP_GRID_IPAD_PRO_MAX,
  BOOTSTRAP_GRID_LG_MAX,
  BOOTSTRAP_GRID_SM_MAX,
} from "constants-lib/dimensions"
import React, { useEffect, useRef, useState } from "react"
import ContentLoader, { List } from "react-content-loader"
import useFeatureFlags from "hooks-lib/LaunchDarkly/useFeatureFlags"
import Slider from "react-slick"
import { ReactSVG } from "react-svg"
import { makeStyles } from "tss-react/mui"
import getContentUrl from "utils-lib/getContentUrl"
import getLanguageRoute from "utils-lib/getLanguageRoute"
import { getWindow } from "utils-lib/getWindow"
import { isHomePage } from "utils-lib/route"
import { isPdfUrl } from "utils-lib/validation/validateHashTagUrl"
import ExceptionMessageContainerModal from "../ExceptionMessageContainerModal/ExceptionMessageContainerModalContainer"
import "./FeaturedContentCards.scss"
import {
  Button,
  ButtonPrimary,
  FormattedTextField,
  Link,
  GenericModal,
} from "../../Base"

export const strings = {
  en_CA: {
    PROCEED_LOGIN_CONTENT:
      "To proceed, log in to My WM. If you do not have a My WM profile, you can create one ",
    CLICK_CONTINUE: "Continue",
    LOGIN_CONTINUE: "Login to continue",
    CLICK_HERE: "here",
  },
  fr_CA: {
    PROCEED_LOGIN_CONTENT:
      "To proceed, log in to My WM. If you do not have a My WM profile, you can create one ",
    CLICK_CONTINUE: "Continue",
    LOGIN_CONTINUE: "Login to continue",
    CLICK_HERE: "here",
  },
}

const ssrWindow = getWindow()

const serviceTypeSwitch = (serviceType) =>
  ({
    temporary_bin: `ROLLOFF`,
    permanent_bin: `COMMERCIAL`,
    curb_side: `RESIDENTIAL`,
  }[serviceType])

const useStyles = makeStyles()((theme) => ({
  root: {
    marginBottom: "40px !important",
    "& .slick-list": {
      [theme.breakpoints.down(`lg`)]: {
        width: `100% !important`,
      },
    },
    "& .slick-dots button:focus": {
      outline: "2px auto -webkit-focus-ring-color",
    },
    "& .slick-dots li button:before": {
      fontSize: ` 12px !important`,
      lineHeight: ` 13px !important`,
      zIndex: `1 !important`,
      width: `100% !important`,
      height: `100% !important`,
    },
    "& .slick-dots li.slick-active button:before": {
      display: `none !important`,
      zIndex: `1 !important`,
      width: `100% !important`,
      height: `100% !important`,
    },
    "& .slick-arrow.slick-prev:before, & .slick-arrow.slick-next:before": {
      display: `none  !important`,
    },
    "& .slick-next": {
      "&:focus": {
        outline: `2px auto -webkit-focus-ring-color !important`,
      },
      [theme.breakpoints.up(`lg`)]: {
        right: `-54px !important`,
      },
      [theme.breakpoints.down(`lg`)]: {
        display: `none !important`,
      },
    },
    "& .slick-prev": {
      "&:focus": {
        outline: `2px auto -webkit-focus-ring-color !important`,
      },
      [theme.breakpoints.up(`lg`)]: {
        left: `-54px !important`,
      },
      [theme.breakpoints.down(`lg`)]: {
        display: `none !important`,
      },
    },
    "& .slick-slider .prev, .slick-slider .next": {
      display: `none !important`,
      [theme.breakpoints.up(`xl`)]: {
        display: `block !important`,
      },
    },
    "& .slick-track": {
      display: `flex !important`,
    },
    "& .slick-slide": {
      height: `inherit !important`,
      visibility: `visible`,
      opacity: 1,
      transition: `opacity 0.2s ease, visibility 0.2s ease`,
    },
    "& .slick-slide:not(.slick-active)": {
      visibility: `hidden`,
      opacity: 0,
    },
    "& .slick-slide.slick-active, & .slick-slide.slick-center": {
      visibility: `visible`,
      opacity: 1,
    },
    "& .slick-slide > div": {
      height: `100% !important`,
      display: `flex !important`,
      justifyContent: `center !important`,
    },
  },
  arrowWrapper: {
    display: `flex`,
    justifyContent: `space-between`,
    [theme.breakpoints.up(`xl`)]: {
      display: `none`,
    },
    "@media (max-width:375px)": {
      marginTop: "80px",
    },
  },
  arrow: {
    height: `3.125rem`,
    width: `3.125rem`,
    borderRadius: `100%`,
    zIndex: `1`,
    [theme.breakpoints.up(`lg`)]: {
      "&>svg": {
        height: `3.125rem`,
        width: `3.125rem`,
      },

      "&:hover": {
        "&>svg>path": {
          fill: theme.palette.button.primaryHover,
        },
      },
    },
    "&>svg": {
      width: `100%`,
      maxHeight: `100%`,
      height: `auto`,
      objectFit: `fill`,
    },
  },
  dots: {
    [theme.breakpoints.down(`md`)]: {
      bottom: `-55px !important`,
    },
    [theme.breakpoints.down(`sm`)]: {
      bottom: `-45px !important`,
      left: `20% !important`,
      width: `61% !important`,
    },
    [theme.breakpoints.up(`xl`)]: {
      bottom: `-35px !important`,
    },
    "@media (max-width:375px)": {
      bottom: `-125px !important`,
      left: `20% !important`,
      width: `61% !important`,
    },
    "@media (max-width:320px)": {
      bottom: `-135px !important`,
      width: `60% !important`,
      left: `20% !important`,
      "& li": {
        height: `12px !important`,
        width: `12px !important`,
      },
      "& li > button": {
        height: `.5rem !important`,
        width: `.5rem !important`,
        "&::before": {
          height: `8px !important`,
          width: `8px !important`,
        },
      },
      "& .slick-active>button": {
        boxSizing: `border-box !important`,
        height: `8px !important`,
        width: `8px !important`,
      },
    },
    bottom: `-45px !important`,
    left: `12.5% !important`,
    width: `75% !important`,
    "& li > button": {
      height: `.75rem !important`,
      width: `0.75rem !important`,
      "&::before": {
        height: `12px !important`,
        width: `12px !important`,
        borderRadius: `11px !important`,
        color: `${theme.palette.background.green} !important`,
        opacity: `1 !important`,
      },
    },
    "& .slick-active>button": {
      boxSizing: `border-box !important`,
      height: `12px !important`,
      width: `12px !important`,
      border: `2px solid ${theme.palette.background.green} !important`,
      borderRadius: `11px !important`,
    },
  },
  slideFocus: {
    "& button:focus": {
      outline: "6px auto #C8A0A0 !important",
      borderRadius: `6px`,
    },
  },
  backgroundFill: {
    position: `absolute`,
    bottom: `0`,
    left: `0`,
    height: `45%`,
    width: `100vw`,
  },
  carouselHeading: {
    height: `48px`,
    width: `838px`,
    color: `#024731`,
    fontSize: `44px`,
    fontWeight: `900`,
    letterSpacing: `0`,
    lineHeight: `48px`,
  },
  carouselHeadingContainer: { margin: `0 2rem` },
  carouselContent: {
    display: `flex !important`,
    width: `100% !important`,
    flexDirection: `column`,
    alignItems: `center`,
    margin: `0 12px`,
    paddingBottom: `1rem`,
    borderRadius: `.25rem`,
  },
  image: {
    backgroundSize: `cover`,
    position: `absolute`,
    backgroundPosition: `center`,
    zIndex: `1`,
    height: "10rem",
    width: "10rem",
    transform: `translate(-50%, -50%)`,
    top: `20%`,
    borderRadius: `50%`,
  },
  textArea: {
    width: `100%`,
    position: `relative`,
    backgroundColor: theme.palette.background.white,
    overflow: `hidden`,
    paddingLeft: `1.5rem`,
    paddingRight: `1.5rem`,
    marginTop: `5rem`,
    paddingTop: `6rem`,
    display: `flex`,
    flexDirection: `column`,
    justifyContent: `space-around`,
    boxShadow: `0 0.5px 1px 0 rgba(0,0,0,0.1), 0 4px 8px 0 rgba(0,0,0,0.2)`,
    // minHeight: `1px`,
    minHeight: `362px`,
    height: `auto`,
    "@media (max-width:375px)": {
      marginTop: `6.5rem`,
      paddingTop: `6rem`,
      minHeight: `450px`,
    },
  },
  ctaText: {
    padding: `1rem 0`,
  },
  learnMoreTarget: {
    textDecoration: `underline !important`,
    color: `${theme.palette.primary.main} !important`,
    textAlign: "initial",
  },
  eyebrow: {
    color: `${theme.palette.background.darkGreen2_0} !important`,
    fontSize: `14px`,
    fontWeight: `bold`,
    letterSpacing: `1.4px`,
    lineHeight: `14px`,
  },
  ctaBrowseButton: {
    boxShadow: "0 0.8px 1.5px 0 rgba(0,0,0,0.1), 0 6px 12px 0 rgba(0,0,0,0.2)",
  },
  featuredCardsContainers: {
    display: "flex",
    justifyContent: "center",
  },
  cardContainer: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    [theme.breakpoints.down("lg")]: {
      flexDirection: "column",
      alignItems: "center",
    },
  },
  cardStyling: {
    width: "235px",
    height: "310px",
    boxShadow: "1px 4px 4px 1px rgba(0, 0, 0, 0.2)",
    marginRight: "2rem",
    borderRadius: "3px",
    marginTop: "2rem",
    padding: "0",
    "&:hover": {
      cursor: "pointer",
    },
    [theme.breakpoints.down("lg")]: {
      width: "18.3rem",
      height: "7.8rem",
      display: `flex`,
      marginTop: "1rem",
      marginLeft: "1.5rem",
    },
    "@media (min-width:601px) and (max-width: 991px)": {
      marginTop: "2rem",
    },
  },
  contentSection: {
    paddingLeft: "1.5rem",
    marginTop: "1rem",
    color: "#000000 !important",
    "&:hover": {
      cursor: "pointer",
    },
  },
  image_1: {
    backgroundSize: `cover`,
    backgroundPosition: `center`,
    height: "190px",
    width: "235px",
    [theme.breakpoints.down("lg")]: {
      height: "125px",
      width: "130px",
    },
  },
  headingContainer: {
    marginTop: "2rem",
    boxSizing: "border-box",
    "@media (min-width:701px) and (max-width: 1500px)": {
      width: "600px",
    },
  },
  heading: {
    color: `${theme.palette.background.darkGreen} !important`,
    [theme.breakpoints.down("xl")]: {
      maxWidth: "580px",
      margin: "0px auto",
      paddingLeft: "2rem",
    },
    [theme.breakpoints.down("lg")]: {
      maxWidth: "23.75rem",
      margin: "0px auto",
      paddingLeft: "2rem",
    },
  },
  proceedText: {
    paddingBottom: ".4rem",
    "& a": {
      color: "red",
      textDecoration: "underline",
    },
  },
  learnMore: {
    textDecoration: `underline !important`,
  },
}))

const FeaturedContentCards = ({
  componentCTAText,
  componentCTAURL,
  getLiveChat,
  strings,
  fetchDataLocalsite,
  updateCurrentNeighborhood,
  getCityBilledBulkEligibility,
  getGuestContainerRepairEligibility,
  setCustomerLob,
  setGuidedFlowSelections,
  getContactBackAttributes,
  featuredContentCards,
  customerSelections,
  language = ``,
  siteLanguage = "",
  router,
  getCityEligibility,
  isLoggedIn = false,
  analyticsKey = "",
  colorPalette = "no",
  onlineProducts,
  enableBulkFlow = true,
  customerSupportContact,
  resetContainerRepair,
  cityBilledAnalyticsData = {},
  userAccount,
}) => {
  const [loaded, setLoaded] = useState(false)
  const [shouldShowContainerModal, setShouldShowContainerModal] =
    useState(false)
  const [showBulkLoginModal, setShowBulkLoginModal] = useState(false)
  const [bulkLoginClicked, setBulkLoginClicked] = useState(false)
  const [showModalContainer, setShowModalContainer] = useState(false)

  const { LDFFReleaseHomepageBulkPickupLoginStepTransitionText } =
    useFeatureFlags(["LDFFReleaseHomepageBulkPickupLoginStepTransitionText"])

  useEffect(() => {
    setTimeout(() => {
      setLoaded(true)
    }, 1000)
  }, [])

  useEffect(() => {
    if (loaded) {
      defer(() => {
        const el = ssrWindow.document.getElementById("HomepageAddressInput")
        el?.scrollIntoView({ behavior: `smooth` })
      })
    }
  }, [get(featuredContentCards, `[0].cardHeading`, ``)])

  useEffect(() => {
    if (
      customerSelections?.cityBilledEligibilityStatus === "fulfilled" &&
      customerSelections?.cityBilledEligibility?.showLoginModal &&
      !showBulkLoginModal &&
      bulkLoginClicked &&
      LDFFReleaseHomepageBulkPickupLoginStepTransitionText
    ) {
      setShowBulkLoginModal(true)
    }
  }, [
    customerSelections?.cityBilledEligibilityStatus,
    customerSelections?.cityBilledEligibility?.showLoginModal,
    showBulkLoginModal,
    LDFFReleaseHomepageBulkPickupLoginStepTransitionText,
  ])

  const containerHideModal = () => {
    setShowModalContainer(false)
  }

  const handleClick = (path) => {
    isPdfUrl(path) ? ssrWindow.open(path) : (ssrWindow.location = path)
  }
  const handleCTAClick = (path) => {
    if (endsWith(path, PATHS.CONTAINER_ISSUE)) {
      resetContainerRepair()
      setShowModalContainer(true)
      setShouldShowContainerModal(true)
    }
    if (endsWith(path, PATHS.BULK_TRASH_PICKUP)) {
      const languageRoute = getLanguageRoute(language)
      const isUS = language === LANG_OPTIONS_MAP.EN_US
      const userState = userAccount?.userState
      const isGuestUser = userState !== userType.LOGGED_IN
      if ((isUS || isEmpty(languageRoute)) && isGuestUser) {
        getCityEligibility(
          null,
          LDFFReleaseHomepageBulkPickupLoginStepTransitionText,
        )
        setBulkLoginClicked(true)
      } else {
        getCityEligibility(null, false)
      }
    }
  }
  const sliderRef = useRef(null)

  const onNextClick = () => sliderRef.current.slickNext()
  const onPrevClick = () => sliderRef.current.slickPrev()

  const { classes } = useStyles()

  function renderArrow(direction, onClick, isDesktop = false, colorPalette) {
    const arrowDirection = direction === `next` ? `right` : `left`
    const arrowColor = colorPalette === "green1" ? "white" : "green"
    const buttonFillColor = colorPalette === "green1" ? "yellow" : "green"
    const srcString = isDesktop
      ? getContentUrl(
          `/content/dam/wm/icons/controls/pagination/${buttonFillColor}-circle-shadow-arrow-${arrowDirection}.svg`,
        )
      : getContentUrl(
          `/content/dam/wm/icons/controls/20x20/${arrowColor}/arrow-${arrowDirection}.svg`,
        )

    let buttonSlideArrow
    if (direction === `next`) {
      buttonSlideArrow = `Next Slide`
    } else {
      buttonSlideArrow = `Previous Slide`
    }

    return (
      <Button
        analytics_key={`ProductCarousel ${direction}`}
        className={`review-carousel-${direction}`}
        onClick={onClick}
        ariaLabel={buttonSlideArrow}
      >
        <ReactSVG
          wrapper="div"
          className={`${classes.arrow} ${direction}`}
          src={srcString}
        />
      </Button>
    )
  }

  // isLoading
  const isLoading = () =>
    /*
      Don't show loader on home page to give the perception that the page loads faster than it
      actually does.
    */
    !isHomePage() &&
    customerSelections.getCombinedProductUrlStatus === `pending`

  // Title loader
  const renderTitleLoader = () => (
    <ContentLoader
      width="100%"
      height="50"
      style={{
        width: `100%`,
        height: `50px`,
      }}
    >
      <rect x="0" y="0" rx="5" ry="5" width="100%" height="50" />
    </ContentLoader>
  )

  // Link Loader
  const renderLinkLoader = () => (
    <ContentLoader
      width="100%"
      height="20"
      style={{
        width: `100%`,
        height: `20px`,
      }}
    >
      <rect x="0" y="0" rx="5" ry="5" width="100%" height="20" />
    </ContentLoader>
  )

  const getBinType = (serviceType) => serviceTypeSwitch(serviceType)

  const shouldHide = (serviceType) => {
    const formattedAddress = get(
      customerSelections,
      `customerAddress.formattedAddress`,
      ``,
    )
    const serviceTypeValue = getBinType(serviceType)

    if (serviceTypeValue && formattedAddress !== ``) {
      // if binType is defined and there is an address
      if (
        get(customerSelections, `getCombinedProductUrlStatus`, `failed`) ===
        `fulfilled`
      ) {
        const productValue = get(
          customerSelections,
          `combinedProductSupportData.productResults.${serviceTypeValue}`,
          {},
        )
        // if grouped api succeeds
        if (
          !isEmpty(productValue) // checking for product details
        ) {
          if (productValue.indexOf("http") === -1) {
            // checking for CC url
            return true
          }
        } else {
          return true
        }
      }
    }
    return false
  }

  // Redirect to service page
  const redirectToGuestServicePage = () => {
    /* Redirect to the services page. */
    ssrWindow.location = `${getLanguageRoute(language)}/services`
  }

  // link redirect Logic

  const onButtonArrowClick = (hyperlinkPath, serviceType) => {
    const formattedAddress = get(
      customerSelections,
      `customerAddress.formattedAddress`,
      ``,
    )
    const zipCodeAndCity = get(customerSelections, `customerAddress`, ``)
    const { city, postalCode } = zipCodeAndCity

    if (formattedAddress !== `` && (!city || !postalCode)) {
      router.push(hyperlinkPath)
    } else {
      const formattedAddress = get(
        customerSelections,
        `customerAddress.formattedAddress`,
        ``,
      )
      const actionUrl = get(
        customerSelections,
        `combinedProductSupportData.productResults.${getBinType(serviceType)}`,
      )
      if (formattedAddress !== `` && actionUrl && actionUrl !== NO) {
        ssrWindow.location = actionUrl
      } else if (hyperlinkPath) {
        router.push(hyperlinkPath)
      } else {
        redirectToGuestServicePage()
      }
    }
  }

  // Rendering Carousel items
  function renderCarouselItems(filteredFeaturedCardDetails) {
    return map(filteredFeaturedCardDetails, (item) => (
      <div
        key={`category-${item.cardHeading}`}
        className={`maax-font-weight-regular ${classes.carouselContent}`}
      >
        <div>
          <img
            className={`${classes.image}`}
            src={getContentUrl(item.imagePath)}
            alt={item?.altText}
            width={160}
            height={160}
          />
        </div>
        <div className={classes.textArea}>
          {isLoading() === true ? (
            renderTitleLoader()
          ) : (
            <Box py={2}>
              <Typography
                className="maax-font-weight-bold wm-text-gray-dark"
                variant="h4"
                component="h3"
              >
                {item.cardHeading}
              </Typography>
            </Box>
          )}
          {isLoading() === true ? (
            <List />
          ) : (
            <Box>
              <FormattedTextField
                variant="body1"
                color="textPrimary"
                className="maax-fgont-weight-regular"
              >
                {item.cardBody}
              </FormattedTextField>
            </Box>
          )}

          {isEmpty(item.cardServiceType) || !item.cardServiceType ? (
            item.cardCTAText && item.cardCTAURL ? (
              <div className={`${classes.ctaText} maax-font-weight-regular`}>
                {isLoading() === true ? (
                  renderLinkLoader()
                ) : (
                  <Link
                    isExternalLink={item.isExternalLink}
                    href={
                      endsWith(item.cardCTAURL, PATHS.BULK_TRASH_PICKUP) ||
                      endsWith(item.cardCTAURL, PATHS.CONTAINER_ISSUE)
                        ? ``
                        : item.cardCTAURL
                    }
                    className={classes.learnMore}
                    target="_self"
                    analytics_key={item.analyticsKey || item.cardHeading}
                    onClick={() => {
                      handleCTAClick(item.cardCTAURL)
                    }}
                  >
                    {item.cardCTAText}
                  </Link>
                )}
              </div>
            ) : null
          ) : isLoading() === true ? (
            renderLinkLoader()
          ) : /*
            IF (product API pending AND service type matches) OR grouped API is pending
            {
              IF combined from grouped API is shown
                show loader
              ELSE IF product API is not pending OR type does not match selected type
                show link
              ELSE
                return nothing
            }
          */
          (customerSelections.getProductsUrlStatus === `pending` &&
              item.cardServiceType === customerSelections.productType) ||
            customerSelections.getCombinedProductUrlStatus === `pending` ? (
            customerSelections.showCombinedProduct && (
              <ReactSVG
                wrapper="span"
                className="loaderIcon"
                src={getContentUrl("/content/dam/wm/icons/loader-icon.svg")}
              />
            )
          ) : (
            (customerSelections.getProductsUrlStatus !== `pending` ||
              item.cardServiceType !== customerSelections.productType) && (
              <div className={`${classes.ctaText} maax-font-weight-regular`}>
                <Link
                  isExternalLink={item.externalLink}
                  className={classes.learnMore}
                  target="_blank"
                  rel="noopener noreferrer"
                  analytics_key={item.analyticsKey || item.cardHeading}
                  onClick={() => {
                    onButtonArrowClick(item.cardCTAURL, item.cardServiceType)
                  }}
                >
                  {item.cardCTAText}
                </Link>
              </div>
            )
          )}

          <div>
            {item.cardServiceType === customerSelections.productType &&
              customerSelections.getProductsUrlStatus === `fulfilled` && (
                <UrlStatusMessageContainer lob={item.cardServiceType} />
              )}
          </div>
        </div>
      </div>
    ))
  }

  const isDesktop = ssrWindow.innerWidth > BOOTSTRAP_GRID_IPAD_PRO_MAX

  // Adding previous logic here
  const userProducts = get(customerSelections, `userProducts`, {})

  const hideCards =
    isLoggedIn &&
    !isEmpty(userProducts) &&
    ssrWindow.location.pathname.split("/").length <= 3

  if (isEmpty(featuredContentCards) || hideCards) {
    return null
  }

  const filteredFeaturedCardDetails = filter(
    featuredContentCards,
    (cardData) => !shouldHide(cardData.cardServiceType),
  )

  const style = {}

  const noNegativeMargin = ssrWindow.location.pathname.split("/").length > 3

  const renderBulkLoginModal = () => {
    const languageRoute = getLanguageRoute(language)
    const US_EN_ROUTE = `/us/en`
    const baseRoute = isEmpty(languageRoute) ? US_EN_ROUTE : languageRoute

    return (
      <Box>
        <Typography variant="subtitle2" color="textPrimary" className="mb-2">
          {strings.LOGIN_CONTINUE}
        </Typography>
        <Typography variant="body1" color="textPrimary" className="proceedText">
          {strings.PROCEED_LOGIN_CONTENT}
          <Link
            className={`${classes.learnMore} mb-1`}
            href={`${baseRoute}/user/register`}
            isExternalLink
          >
            {strings.CLICK_HERE}
          </Link>
          .
        </Typography>
        <Box mt={2}>
          <ButtonPrimary
            onClick={() => {
              setShowBulkLoginModal(false)
              setBulkLoginClicked(false)
              ssrWindow.location = `${baseRoute}/user/login?redirect=/mywm/user/my-services`
            }}
            className={`${classes.ctaBrowseButton} w-100`}
          >
            {strings.CLICK_CONTINUE}
          </ButtonPrimary>
        </Box>
      </Box>
    )
  }

  return renderCarouselItems(filteredFeaturedCardDetails).length !== 0 ? (
    <>
      <div data-testid="FeaturedContentCards" className="wm-bg-gray">
        <div
          id="FeaturedContentCards"
          className={`ProductCarousel maax-font-weight-regular pt-md-4 pt-0 ${
            classes.root
          } ${noNegativeMargin ? `mt-0` : ``}`}
          style={style}
        >
          <div className="container tab-width">
            <div className={`backgroundFill ${classes.backgroundFill}`} />
            <Slider
              ref={sliderRef}
              dots
              appendDots={(dots) => (
                <ul aria-label="Carousel Slides">
                  {dots.map((item, index) => {
                    const dotsClassName = item?.props?.className
                    return (
                      <li className={dotsClassName} key={index}>
                        {React.cloneElement(item.props.children, {
                          "aria-pressed": dotsClassName === "slick-active",
                        })}
                      </li>
                    )
                  })}
                </ul>
              )}
              infinite
              slidesToShow={
                renderCarouselItems(filteredFeaturedCardDetails).length >= 3
                  ? 3
                  : renderCarouselItems(filteredFeaturedCardDetails).length
              }
              slidesToScroll={
                renderCarouselItems(filteredFeaturedCardDetails).length >= 3
                  ? 2
                  : renderCarouselItems(filteredFeaturedCardDetails).length
              }
              nextArrow={renderArrow(
                `next`,
                onNextClick,
                isDesktop,
                colorPalette,
              )}
              prevArrow={renderArrow(
                `prev`,
                onPrevClick,
                isDesktop,
                colorPalette,
              )}
              dotsClass={`slick-dots ${classes.dots} ${classes.slideFocus}`}
              responsive={[
                {
                  breakpoint: BOOTSTRAP_GRID_SM_MAX,
                  settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                  },
                },
                {
                  breakpoint: BOOTSTRAP_GRID_LG_MAX,
                  settings: {
                    slidesToShow:
                      renderCarouselItems(filteredFeaturedCardDetails)
                        .length === 2
                        ? 1
                        : 2,
                    slidesToScroll: 1,
                  },
                },
              ]}
            >
              {renderCarouselItems(filteredFeaturedCardDetails)}
            </Slider>
            {filteredFeaturedCardDetails.length > 1 && (
              <Box
                className={`${classes.arrowWrapper} ${
                  ssrWindow.innerWidth > BOOTSTRAP_GRID_IPAD_PRO_MAX
                    ? "carouselIcon"
                    : ""
                }`}
              >
                {renderArrow(`prev`, onPrevClick, isDesktop, colorPalette)}
                {renderArrow(`next`, onNextClick, isDesktop, colorPalette)}
              </Box>
            )}
          </div>
        </div>
        {componentCTAText && componentCTAURL ? (
          <div
            className="FeaturedCtaButton"
            style={{
              textAlign: "center",
              paddingBottom: "3%",
              paddingTop: "1%",
            }}
          >
            <ButtonPrimary
              analytics_key={analyticsKey}
              onClick={() => handleClick(componentCTAURL)}
              className={`${classes.ctaBrowseButton}`}
            >
              {componentCTAText}
            </ButtonPrimary>
          </div>
        ) : null}
        {shouldShowContainerModal && (
          <ExceptionMessageContainerModal
            customerSelections={customerSelections}
            filteredFeaturedCardDetails={filteredFeaturedCardDetails}
            getLiveChat={getLiveChat}
            shouldShowContainerModal={shouldShowContainerModal}
            fetchDataLocalsite={fetchDataLocalsite}
            updateCurrentNeighborhood={updateCurrentNeighborhood}
            getCityBilledBulkEligibility={getCityBilledBulkEligibility}
            setCustomerLob={setCustomerLob}
            setGuidedFlowSelections={setGuidedFlowSelections}
            getContactBackAttributes={getContactBackAttributes}
            showModalContainer={showModalContainer}
            setShowModalContainer={containerHideModal}
            onlineProducts={onlineProducts}
            customerSupportContact={customerSupportContact}
            siteLanguage={siteLanguage}
            enableBulkFlow={enableBulkFlow}
            cityBilledAnalyticsData={cityBilledAnalyticsData}
            getGuestContainerRepairEligibility={
              getGuestContainerRepairEligibility
            }
          />
        )}
        {showBulkLoginModal &&
          LDFFReleaseHomepageBulkPickupLoginStepTransitionText && (
            <GenericModal
              showCloseButton
              onClose={() => {
                setShowBulkLoginModal(false)
                setBulkLoginClicked(false)
              }}
            >
              {renderBulkLoginModal()}
            </GenericModal>
          )}
      </div>
    </>
  ) : null
}

// Note: React 19 no-longer supports propTypes. Leaving this for reference.
// FeaturedContentCards.propTypes = {
//   componentCTAText: PropTypes.string.isRequired,
//   componentCTAURL: PropTypes.string.isRequired,
//   getLiveChat: PropTypes.string.isRequired,
//   fetchDataLocalsite: PropTypes.string.isRequired,
//   updateCurrentNeighborhood: PropTypes.string.isRequired,
//   getCityBilledBulkEligibility: PropTypes.string.isRequired,
//   getGuestContainerRepairEligibility: PropTypes.func.isRequired,
//   setCustomerLob: PropTypes.string.isRequired,
//   setGuidedFlowSelections: PropTypes.string.isRequired,
//   getContactBackAttributes: PropTypes.string.isRequired,
//   featuredContentCards: PropTypes.arrayOf(
//     PropTypes.shape({
//       analyticsKey: PropTypes.string,
//       cardBody: PropTypes.string,
//       cardCtaText: PropTypes.string,
//       cardCtaURL: PropTypes.string,
//       cardServiceType: PropTypes.string,
//       cardHeading: PropTypes.string,
//       imagePath: PropTypes.string,
//       externalLink: PropTypes.bool,
//       altText: PropTypes.string,
//     }),
//   ),
//   customerSelections: PropTypes.object.isRequired,
//   language: PropTypes.string,
//   siteLanguage: PropTypes.string,
//   router: PropTypes.object.isRequired,
//   getCityEligibility: PropTypes.func.isRequired,
//   isLoggedIn: PropTypes.bool,
//   analyticsKey: PropTypes.string,
//   colorPalette: PropTypes.string,
//   onlineProducts: PropTypes.object.isRequired,
//   enableBulkFlow: PropTypes.bool,
//   customerSupportContact: PropTypes.object.isRequired,
//   resetContainerRepair: PropTypes.func.isRequired,
//   cityBilledAnalyticsData: PropTypes.object,
// }

// Note: React 19 no-longer supports defaultProps. Leaving this for reference.
// FeaturedContentCards.defaultProps = {
//   cityBilledAnalyticsData: {},
//   enableBulkFlow: true,
//   analyticsKey: "",
//   featuredContentCards: [],
//   language: ``,
//   siteLanguage: "",
//   colorPalette: "",
//   isLoggedIn: false,
// }

// export default FeaturedContentCards
export default withLocalization(strings)(FeaturedContentCards)
